import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Modal, CircularProgress } from '@mui/material';
import { AddCircle, Close, Delete } from '@mui/icons-material';
import { useSwipeable } from 'react-swipeable';
import StoryUpload from '../../components/UserAuthentication/StoryUpload';
import { addStories, getStories, getImage, getUsernameById, deleteStories } from '../../utils/api';
import StoriesComponent from 'react-insta-stories';

const Stories = () => {
    const [stories, setStories] = useState([]);
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [groupedStories, setGroupedStories] = useState({});
    const [userProfiles, setUserProfiles] = useState({});

    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    const currentUserId = localStorage.getItem('userId');
    useEffect(() => {
        const fetchStories = async () => {
            if (!currentUserId) {
                console.warn('No user logged in. Skipping stories fetch.');
                return;
            }
            try {
                const response = await getStories(currentUserId);
                setStories(response.stories || []);
                const grouped = groupStoriesByUser(response.stories || []);
                setGroupedStories(grouped);

                const profiles = {};
                const userIds = Object.values(grouped)
                    .map(group => group.map(item => item.userId))
                    .flat();

                // Fetch profiles only if a valid userId exists.
                for (const userId of userIds) {
                    if (!userId || userId === "null") continue;
                    const userImageResponse = await getImage(userId);
                    const userUsernameres = await getUsernameById(userId);
                    profiles[userId] = {
                        image: userImageResponse,
                        username: userId === currentUserId ? 'Me' : userUsernameres?.username || 'Unknown'
                    };
                }
                // Ensure the current user's profile is fetched even if no story exists
                if (currentUserId && !profiles[currentUserId]) {
                    const userImageResponse = await getImage(currentUserId);
                    const userUsernameRes = await getUsernameById(currentUserId);
                    profiles[currentUserId] = {
                        image: userImageResponse,
                        username: 'Me', // or userUsernameRes?.username if you prefer
                    };
                }
                setUserProfiles(profiles);
            } catch (error) {
                console.error('Error fetching stories or user profiles:', error.message);
            }
        };

        fetchStories();
    }, [currentUserId]);

    const groupStoriesByUser = (stories) => {
        const grouped = {};
        stories.forEach((story) => {
            if (!grouped[story.userId]) {
                grouped[story.userId] = [];
            }
            grouped[story.userId].push(story);
        });
        return grouped;
    };

    const handleUploadClick = () => {
        setIsUploadOpen(true);
    };

    const handleUpload = async (uploadedFile) => {
        setIsUploading(true);
        const formData = new FormData();
        formData.append('userId', currentUserId);
        formData.append('file', uploadedFile);
        try {
            const response = await addStories(formData, (progress) => {
                setUploadProgress(progress);
            });
            const newStories = [response.story, ...stories];
            setStories(newStories);
            setGroupedStories(groupStoriesByUser(newStories));
            setUploadProgress(0);
        } catch (error) {
            console.error('Error uploading story:', error.message);
            alert('Failed to upload story. Please try again.');
            setUploadProgress(0);

        } finally {
            setIsUploading(false);
            setIsUploadOpen(false);
        }
    };

    const handleStoryClick = (userId, index) => {
        setSelectedUserId(userId);
        setCurrentStoryIndex(index);
        setIsModalOpen(true);
    };

    const handleNextStory = () => {
        const userStories = groupedStories[selectedUserId];
        if (currentStoryIndex < userStories.length - 1) {
            setCurrentStoryIndex(currentStoryIndex + 1);
        } else {
            // If it's not the current user and no more stories remain, close modal or move to next user
            if (selectedUserId !== currentUserId) {
                const userIds = Object.keys(groupedStories);
                const currentUserIndex = userIds.indexOf(selectedUserId);
                if (currentUserIndex < userIds.length - 1) {
                    const nextUserId = userIds[currentUserIndex + 1];
                    setSelectedUserId(nextUserId);
                    setCurrentStoryIndex(0);
                } else {
                    setIsModalOpen(false);
                }
            } else {
                setIsModalOpen(false);
            }
        }
    };

    const handlePreviousStory = () => {
        if (currentStoryIndex > 0) {
            setCurrentStoryIndex(currentStoryIndex - 1);
        } else if (selectedUserId !== currentUserId) {
            const userIds = Object.keys(groupedStories);
            const currentUserIndex = userIds.indexOf(selectedUserId);
            if (currentUserIndex > 0) {
                const prevUserId = userIds[currentUserIndex - 1];
                setSelectedUserId(prevUserId);
                setCurrentStoryIndex(groupedStories[prevUserId].length - 1);
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedUserId(null);
    };

    // Delete function now checks if the story belongs to the current user before deletion.
    const handleDeleteCurrentStory = async () => {
        const currentStory = groupedStories[selectedUserId][currentStoryIndex];
        if (!currentStory) return;

        // Check if the current story belongs to the logged in user.
        if (currentStory.userId !== currentUserId) {
            console.error("You are not authorized to delete this story.");
            return;
        }

        try {
            await deleteStories(currentStory._id);
            // Update the grouped stories and overall stories list
            const updatedUserStories = groupedStories[selectedUserId].filter(
                (story, index) => index !== currentStoryIndex
            );
            const updatedGrouped = { ...groupedStories };
            if (updatedUserStories.length) {
                updatedGrouped[selectedUserId] = updatedUserStories;
            } else {
                delete updatedGrouped[selectedUserId];
                setIsModalOpen(false);
            }
            setGroupedStories(updatedGrouped);
            setStories(prevStories => prevStories.filter(story => story._id !== currentStory._id));
        } catch (error) {
            console.error('Error deleting story:', error);
        }
    };

    const otherUsersStories = Object.keys(groupedStories).filter(
        (userId) => userId !== currentUserId
    );

    const swipeHandlers = useSwipeable({
        onSwipedLeft: handleNextStory,
        onSwipedRight: handlePreviousStory,
        preventDefaultTouchmoveEvent: true,
        trackMouse: true
    });

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: '15px',
                    padding: '10px',
                    backgroundColor: '#f9f9f9',
                    alignItems: 'center',
                    borderBottom: '0.5px solid #e0e0e0',
                    maxWidth: '100%',
                }}
            >
                {/* Current User's Story with + Button */}
                <Box
                    sx={{
                        position: 'relative',
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        backgroundColor: '#ddd',
                        border: '2px solid #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    {userProfiles[currentUserId]?.image?.image ? (
                        <img
                            src={userProfiles[currentUserId].image.image}
                            alt="My Story"
                            style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                objectFit: 'cover',
                            }}
                            onClick={() => groupedStories[currentUserId]?.length > 0 && handleStoryClick(currentUserId, 0)}
                        />
                    ) : (
                        <Typography></Typography>
                    )}
                    {currentUserId && (
                        <IconButton
                            sx={{
                                position: 'absolute',
                                bottom: -15,
                                left: 25,
                            }}
                            onClick={handleUploadClick}
                        >
                            <AddCircle />
                        </IconButton>
                    )}
                </Box>

                {/* Display One Story Per Other User */}
                {otherUsersStories.map((userId) => (
                    <Box
                        key={userId}
                        sx={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            backgroundColor: '#ddd',
                            border: '2px solid #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                        onClick={() => handleStoryClick(userId, 0)}
                    >
                        {userProfiles[userId]?.image?.image ? (
                            <img
                                src={userProfiles[userId].image.image}
                                alt="User Story"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                }}
                            />
                        ) : (
                            <Typography>{userProfiles[userId]?.username}</Typography>
                        )}
                    </Box>
                ))}
            </Box>

            {/* Image Upload Dialog */}
            <StoryUpload
                open={isUploadOpen}
                onClose={() => setIsUploadOpen(false)}
                onUpload={handleUpload}
                isUploading={isUploading}
                uploadProgress={uploadProgress}
            />

            {/* Story Modal */}
            {isModalOpen && (
                <Modal
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'black',
                            overflow: 'hidden',
                        }}
                        {...swipeHandlers}
                    >
                        {/* Top-Right Controls */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                zIndex: 9999,
                                display: 'flex',
                                gap: 1,
                                pointerEvents: 'auto',
                            }}
                        >
                            {selectedUserId === currentUserId && (
                                <IconButton onClick={handleDeleteCurrentStory} sx={{ color: 'white' }}>
                                    <Delete />
                                </IconButton>
                            )}
                            <IconButton onClick={handleCloseModal} sx={{ color: 'white' }}>
                                <Close />
                            </IconButton>
                        </Box>

                        {/* Stories Component */}
                        {selectedUserId && (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    pointerEvents: 'auto',
                                }}
                            >
                                <StoriesComponent
                                    stories={groupedStories[selectedUserId].map((story) => {
                                        if (story.fileType.startsWith('video')) {
                                            return {
                                                url: `https://pilatesnflow.onrender.com/stories/stream/${story.fileId}`,// `http://localhost:5000/stories/stream/${story.fileId}`,//
                                                type: 'video',
                                                header: {
                                                    heading: userProfiles[selectedUserId]?.username || 'Unknown',
                                                    profileImage: userProfiles[selectedUserId]?.image?.image || '',
                                                },
                                            };
                                        } else {
                                            return {
                                                url: `data:${story.mimeType};base64,${story.fileBlob}`,
                                                type: 'image',
                                                header: {
                                                    heading: userProfiles[selectedUserId]?.username || 'Unknown',
                                                    profileImage: userProfiles[selectedUserId]?.image?.image || '',
                                                },
                                            };
                                        }
                                    })}
                                    defaultInterval={5000}
                                    width="100%"
                                    height="100%"
                                    onStoryEnd={handleNextStory}
                                    onAllStoriesEnd={() => setIsModalOpen(false)}
                                    keyboardNavigation
                                />
                            </Box>
                        )}
                    </Box>
                </Modal>
            )}
        </>
    );
};

export default Stories;
